import React, { useState } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import TermsAccordion from '../components/TermsAccordion';
import PrivacyInfoBlock from '../components/PrivacyInfoBlock';

const ROLES = [
  'Artist', 'Designer', 'Curation & Research', 'Museum/Gallery',
  'Marketing & Communications', 'Partnerships', 'Events & Production',
  'Education', 'Operations & Administration', 'Writing & Editorial', 'Other'
];

const CAREER_STAGES = [
  'Student', 'Emerging', 'Early Career', 'Mid-Level', 'Senior/Leadership'
];

const OPPORTUNITY_OPTIONS = [
  'Full-time', 'Freelance/Gigs', 'Internships', 'Collaborations',
  'Speaking', 'Residencies/Open Calls', 'Volunteer'
];

const OPEN_TO_OPTIONS = ['Remote work', 'Travel', 'Relocation'];

const TalentNetworkPage = () => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);
  const [emailOptIn, setEmailOptIn] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    city: '',
    country: 'India',
    primary_role: '',
    current_role_description: '',
    portfolio_url: '',
    linkedin_url: '',
    instagram_url: '',
    career_stage: '',
    opportunities_sought: [],
    career_goal: '',
    skills: '',
    open_to: [],
    about: '',
    resume_link: ''
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const toggleMulti = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter((v) => v !== value)
        : [...prev[field], value]
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf' && file.size <= 5 * 1024 * 1024) {
      setResumeFile(file);
    } else if (file) {
      toast.error('Please upload a PDF file under 5MB.');
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.primary_role || !formData.career_stage) {
      toast.error('Please select your role and career stage.');
      return;
    }
    if (!termsAgreed) {
      setShowTermsError(true);
      toast.error('Please accept the Terms & Conditions.');
      return;
    }
    setSubmitting(true);

    try {
      const payload = { ...formData, email_opt_in: emailOptIn };

      if (resumeFile) {
        const reader = new FileReader();
        const base64 = await new Promise((resolve) => {
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(resumeFile);
        });
        payload.resume_base64 = base64;
        payload.resume_filename = resumeFile.name;
      }

      await axios.post(`${API_URL}/api/talent`, payload);
      setSubmitted(true);
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F0EBE2' }}>
        <Navbar />
        <div className="px-6 md:px-12 lg:px-24 py-32">
          <div className="max-w-xl mx-auto text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: 'rgba(212,130,10,0.12)' }}>
              <svg className="w-7 h-7" style={{ color: '#D4820A' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-3xl md:text-4xl font-normal tracking-tight mb-4" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0F2942' }}>
              You&rsquo;re In
            </h1>
            <p className="text-base mb-2" style={{ color: '#0F2942' }}>
              Thank you for joining the CultureCareers Professional Talent Network.
            </p>
            <p className="text-sm" style={{ color: '#0F2942' }}>
              We will be in touch when the right opportunity comes up.
            </p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F0EBE2' }}>
      <Navbar />

      {/* Hero */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28" style={{ backgroundColor: '#0F2942' }}>

        <SEO
          title="Professional Talent Network"
          path="/talent/professional"
          description="Get found by galleries, museums and studios. Join CultureCareers' curated professional talent network for artists, designers, curators and creative pros in India."
        />
        <div className="max-w-3xl">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-tight mb-6 text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Professional Talent Network
          </h1>
          <div className="w-20 h-px mb-8" style={{ backgroundColor: 'rgba(212,130,10,0.6)' }} />
          <p className="text-base md:text-lg leading-relaxed font-light max-w-2xl" style={{ color: 'rgba(240, 235, 226, 0.72)' }}>
            CultureCareers connects talented creative professionals with opportunities in galleries, museums, festivals, production houses, cultural NGOs, and beyond. Join our network and we will reach out when the right opportunity comes up.
          </p>
        </div>
      </section>

      {/* Photo banner */}
      <figure className="relative" data-testid="talent-banner-photo">
        <img
          src="https://images.unsplash.com/photo-1719935115623-4857df23f3c6?w=1800&q=70&auto=format&fit=crop"
          alt="A figure in front of a projection at a contemporary art exhibition. Photo by Alice Kotlyarenko on Unsplash"
          loading="lazy"
          decoding="async"
          width="1800"
          height="500"
          className="w-full h-[220px] md:h-[320px] object-cover"
        />
      </figure>

      {/* Form */}
      <section className="px-6 md:px-12 lg:px-24 py-16 md:py-20">
        <div className="max-w-3xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-12 bg-white rounded-xl p-8 md:p-10 shadow-sm" data-testid="talent-form">

            {/* 1. Basic Information */}
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-medium mb-6" style={{ color: '#D4820A' }}>Basic Information</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="md:col-span-2">
                  <label className="text-sm font-medium mb-2 block">Full Name *</label>
                  <Input value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} required className="rounded-lg" placeholder="Your full name" data-testid="talent-name" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Email *</label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="rounded-lg" placeholder="you@example.com" data-testid="talent-email" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Phone Number (for WhatsApp contact)</label>
                  <Input type="tel" inputMode="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="rounded-lg" placeholder="+91 98765 43210" data-testid="talent-phone" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium mb-2 block">City *</label>
                  <Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} required className="rounded-lg" placeholder="e.g. Mumbai" data-testid="talent-city" />
                </div>
              </div>
            </div>

            <div style={{ borderTop: '1px solid #F0EBE2' }} />

            {/* 2. Role & Function */}
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-medium mb-6" style={{ color: '#D4820A' }}>Role & Function</p>
              <div className="space-y-5">
                <div>
                  <label className="text-sm font-medium mb-2 block">Primary Role / Function *</label>
                  <Select value={formData.primary_role} onValueChange={(val) => setFormData({ ...formData, primary_role: val })}>
                    <SelectTrigger className="rounded-lg" data-testid="talent-role">
                      <SelectValue placeholder="Select your primary role" />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">What best describes your current role?</label>
                  <Input value={formData.current_role_description} onChange={(e) => setFormData({ ...formData, current_role_description: e.target.value })} className="rounded-lg" placeholder="e.g. Freelance graphic designer working with cultural brands" data-testid="talent-role-desc" />
                </div>
              </div>
            </div>

            <div style={{ borderTop: '1px solid #F0EBE2' }} />

            {/* 3. Work & Presence */}
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-medium mb-6" style={{ color: '#D4820A' }}>Work & Presence</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div>
                  <label className="text-sm font-medium mb-2 block">Portfolio / Website</label>
                  <Input value={formData.portfolio_url} onChange={(e) => setFormData({ ...formData, portfolio_url: e.target.value })} className="rounded-lg" placeholder="https://..." data-testid="talent-portfolio" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">LinkedIn</label>
                  <Input value={formData.linkedin_url} onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })} className="rounded-lg" placeholder="linkedin.com/in/..." data-testid="talent-linkedin" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Instagram</label>
                  <Input value={formData.instagram_url} onChange={(e) => setFormData({ ...formData, instagram_url: e.target.value })} className="rounded-lg" placeholder="@handle" data-testid="talent-instagram" />
                </div>
              </div>
            </div>

            <div style={{ borderTop: '1px solid #F0EBE2' }} />

            {/* 4. Career Stage */}
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-medium mb-6" style={{ color: '#D4820A' }}>Career Stage</p>
              <div>
                <label className="text-sm font-medium mb-2 block">Where are you in your career? *</label>
                <Select value={formData.career_stage} onValueChange={(val) => setFormData({ ...formData, career_stage: val })}>
                  <SelectTrigger className="rounded-lg" data-testid="talent-stage">
                    <SelectValue placeholder="Select career stage" />
                  </SelectTrigger>
                  <SelectContent>
                    {CAREER_STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div style={{ borderTop: '1px solid #F0EBE2' }} />

            {/* 5. Opportunities Sought */}
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-medium mb-6" style={{ color: '#D4820A' }}>Opportunities Sought</p>
              <div className="space-y-5">
                <div>
                  <label className="text-sm font-medium mb-3 block">What opportunities are you looking for?</label>
                  <div className="flex flex-wrap gap-2" data-testid="talent-opportunities">
                    {OPPORTUNITY_OPTIONS.map((opt) => (
                      <button key={opt} type="button" onClick={() => toggleMulti('opportunities_sought', opt)}
                        className="px-4 py-2 text-sm border rounded-lg transition-colors"
                        style={formData.opportunities_sought.includes(opt)
                          ? { backgroundColor: '#0F2942', color: '#FFFFFF', borderColor: '#0F2942' }
                          : { backgroundColor: '#FFFFFF', color: '#0F2942', borderColor: '#F0EBE2' }
                        }
                      >{opt}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">What are you currently trying to move towards?</label>
                  <Input value={formData.career_goal} onChange={(e) => setFormData({ ...formData, career_goal: e.target.value })} className="rounded-lg" placeholder="e.g. Transition from freelance to a full-time curatorial role" data-testid="talent-goal" />
                </div>
              </div>
            </div>

            <div style={{ borderTop: '1px solid #F0EBE2' }} />

            {/* 6. Skills */}
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-medium mb-6" style={{ color: '#D4820A' }}>Skills & Expertise</p>
              <div>
                <label className="text-sm font-medium mb-2 block">Key skills</label>
                <Input value={formData.skills} onChange={(e) => setFormData({ ...formData, skills: e.target.value })} className="rounded-lg" placeholder="e.g. Graphic design, Exhibition curation, Art writing, Social media" data-testid="talent-skills" />
              </div>
            </div>

            <div style={{ borderTop: '1px solid #F0EBE2' }} />

            {/* 7. Availability */}
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-medium mb-6" style={{ color: '#D4820A' }}>Availability & Flexibility</p>
              <div>
                <label className="text-sm font-medium mb-3 block">Are you open to:</label>
                <div className="flex flex-wrap gap-4" data-testid="talent-open-to">
                  {OPEN_TO_OPTIONS.map((opt) => (
                    <div key={opt} className="flex items-center gap-2">
                      <Checkbox
                        id={`open-${opt}`}
                        checked={formData.open_to.includes(opt)}
                        onCheckedChange={() => toggleMulti('open_to', opt)}
                      />
                      <label htmlFor={`open-${opt}`} className="text-sm" style={{ color: '#0F2942' }}>{opt}</label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ borderTop: '1px solid #F0EBE2' }} />

            {/* 8. About */}
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-medium mb-6" style={{ color: '#D4820A' }}>About You</p>
              <div>
                <label className="text-sm font-medium mb-2 block">Tell us about your work or experience (2-3 lines)</label>
                <Textarea value={formData.about} onChange={(e) => setFormData({ ...formData, about: e.target.value })} rows={3} className="rounded-lg" placeholder="A brief intro about what you do and what you're looking for..." data-testid="talent-about" />
              </div>
            </div>

            <div style={{ borderTop: '1px solid #F0EBE2' }} />

            {/* 9. Resume */}
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-medium mb-6" style={{ color: '#D4820A' }}>Resume (Optional)</p>
              <p className="text-xs mb-4" style={{ color: '#0F2942' }}>Upload a PDF or paste a link to your resume / CV.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="text-sm font-medium mb-2 block">Upload PDF (max 5MB)</label>
                  <Input type="file" accept=".pdf" onChange={handleFileChange} className="rounded-lg" data-testid="talent-resume-upload" />
                  {resumeFile && <p className="text-xs mt-1" style={{ color: '#D4820A' }}>{resumeFile.name}</p>}
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Or paste a link</label>
                  <Input value={formData.resume_link} onChange={(e) => setFormData({ ...formData, resume_link: e.target.value })} className="rounded-lg" placeholder="Google Drive, Dropbox, etc." data-testid="talent-resume-link" />
                </div>
              </div>
            </div>

            <div style={{ borderTop: '1px solid #F0EBE2' }} />

            {/* Terms & Conditions */}
            <TermsAccordion
              variant="talent"
              agreed={termsAgreed}
              onAgreeChange={setTermsAgreed}
              showError={showTermsError}
            />

            {/* Optional Email Opt-In */}
            <div className="flex items-start gap-2.5 -mt-2" data-testid="email-opt-in-wrapper">
              <Checkbox
                id="email-opt-in"
                checked={emailOptIn}
                onCheckedChange={setEmailOptIn}
                className="mt-0.5"
                data-testid="email-opt-in-checkbox"
              />
              <label
                htmlFor="email-opt-in"
                className="text-sm cursor-pointer"
                style={{ color: '#0F2942' }}
              >
                I&rsquo;d like to receive curated opportunities and updates from CultureCareers.
              </label>
            </div>

            {/* Submit */}
            <Button type="submit" disabled={submitting} className="rounded-lg px-8 py-3 font-medium" style={{ backgroundColor: '#0F2942', color: '#FFFFFF' }} data-testid="talent-submit">
              {submitting ? 'Submitting...' : 'Join the Network'}
            </Button>
          </form>
        </div>
      </section>

      <div style={{ backgroundColor: '#F0EBE2' }}>
        <PrivacyInfoBlock
          testid="talent-privacy-info"
          heading="How your information is used"
          points={[
            'Your information is used to connect you with relevant opportunities across the cultural sector and may be shared with organisations where appropriate.',
            'We review submissions to ensure quality and relevance before making any connections.',
            'Your profile is only shared in the context of relevant opportunities. It is not distributed broadly or publicly.',
            'We do not sell your data or use it for unrelated purposes.',
            'You can request access, updates, or removal of your information at any time.'
          ]}
        />
      </div>

      <Footer />
    </div>
  );
};

export default TalentNetworkPage;
